import { useQuery } from "@tanstack/react-query";
import { PermissionService } from "../../../../services/permission/permission.service";
import { useSearchParams } from "../../../../hook/use-search-params";
import { getEnv } from "../../../../AppConfigs";
import { ISharepointLink, ISharepointLinkInfo, ISharepointUser } from "../../../../models/ISharepoint";
import {
  SharePointLinkType,
  SharePointPermissionRole,
  SharePointScope,
} from "../../../../constants/sharePointConstant";
import { useUser } from "../../../../hook";
import { isEqualBetweenSharepointUserAndLoginUser } from "../../../../services/util";
import { useMemo } from "react";

const mapGrandedUserToSharepointUser = (item: any) => {
  if (!item) return undefined;

  if (item.group) {
    const user = item.group as ISharepointUser;
    user.isGroup = true;
    return user;
  } else {
    return item.user as ISharepointUser;
  }
};

const mapShareLinkFromPermission = (item: any) => {
  let users: Array<ISharepointUser> = [];
  const link: ISharepointLinkInfo = item.link ?? {
    type: item.roles.find((r: any) => r === SharePointPermissionRole.read)
      ? SharePointLinkType.view
      : SharePointLinkType.edit,
    webUrl: "",
    scope: SharePointScope.users,
    preventsDownload: false,
  };

  if (item.grantedToV2 || item.grantedTo) {
    const user = mapGrandedUserToSharepointUser(item.grantedToV2 ?? item.grantedTo);
    if (user) users.push(user);
  } else if (item.grantedToIdentitiesV2) {
    users = item.grantedToIdentitiesV2.map((p: any) => mapGrandedUserToSharepointUser(p));
  }

  const result: ISharepointLink = {
    id: item.id,
    roles: item.roles,
    users: users,
    hasPassword: item.hasPassword ?? false,
    link: link,
  };

  return result;
};

export const useDocumentPermission = () => {
  const { searchParams } = useSearchParams();
  const isPublishStatus = searchParams.get("status");
  const fileId = searchParams.get("fileId");
  const status = searchParams.get("status");
  const action = searchParams.get("action");
  const listId = isPublishStatus ? getEnv("REACT_APP_WORKING_DOCS_PUBLISH_INF_LIST_ID") : undefined;
  const { user } = useUser();

  const { data, isLoading } = useQuery({
    queryKey: ["document-permission", fileId],
    queryFn: () => PermissionService.getDocumentPermissonsV2(fileId, listId),
    enabled: !!fileId && status !== "publish" && action === "info",
  });

  const filteredData = useMemo(
    () =>
      !data
        ? []
        : data.map((e: any) => mapShareLinkFromPermission(e)).filter((l: ISharepointLink) => l.users?.length > 0),
    [data],
  );

  const hasWritePermission = useMemo(() => {
    if (user?.isMasterUser) return true;
    if (!filteredData) return true;
    if (
      filteredData.find((p: ISharepointLink) => {
        return (
          p.link.type === SharePointLinkType.edit &&
          p.users &&
          p.users.find((u: ISharepointUser) => isEqualBetweenSharepointUserAndLoginUser(u, user))
        );
      })
    )
      return true;
  }, [filteredData, user]);

  return { filteredData, isLoading, hasWritePermission };
};
